/**
 * Iter XX.71 — ConversationOfferModal
 *
 * Shown ONCE after the user completes FREE_MEANINGFUL_CONVERSATIONS
 * meaningful chats. Bottom-anchored sheet on mobile, centered on
 * desktop. Never interrupts a reply — mounted only after the
 * assistant's response has fully rendered.
 *
 * Trigger contract: Chat.jsx detects `conversation_offer.show === true`
 * in the /web/chat response payload and renders this modal AFTER the
 * assistant message is added to the thread.
 *
 * Data-testids:
 *   conversation-offer-modal
 *   conversation-offer-modal-cta         (primary — Continue with Gully Plus)
 *   conversation-offer-modal-dismiss     (secondary — Not right now)
 */
import React, { useState } from "react";
import api from "../lib/api";
import { readParentMode } from "../lib/parentEntryGate";

// Iter XX.118 (Feb 2026) — Parent-aware continuity messaging.
// The Conversation Offer modal is the "natural transition into
// Gully Plus" — the last surface before the payment screen. It
// must (a) confirm today's coaching was captured, (b) explain
// future sessions build on this one, (c) name the pattern-memory
// benefit, (d) say Gully becomes more personalised over time.
// Copy adapts automatically for player vs parent audience.
function _isParentAudience() {
  try {
    const m = readParentMode();
    return Boolean(m && m.relationship === "parent");
  } catch { return false; }
}

// Iter XX.205 — Player copy. Body is now truthful + personalised
// when `offer.observation` is present (Gully has just noticed a
// specific thing about THIS player). Falls back to the generic
// player copy when no observation exists. Truthfulness rule:
// never claim data will be lost — it stays either way.
const PARENT_COPY = {
  eyebrow: "You’ve had {N} real coaching conversations",
  title:   "Want Gully to keep learning your child’s cricket?",
  body: (
    "You’ve started building a picture of your child’s game. "
    + "With Plus, Gully keeps building on it — noticing patterns "
    + "across matches, practices and conversations that would "
    + "otherwise disappear."
  ),
  bullets: [
    "Every future session builds on what Gully already knows",
    "Pattern memory across matches and months",
    "Coaching that gets more personal to your child over time",
  ],
  dismiss_default: "Not right now",
};
const PLAYER_COPY = {
  eyebrow: "You’ve had {N} real coaching conversations",
  title:   "Want Gully to keep learning your cricket?",
  body: (
    "You’ve started building a picture of your game. "
    + "With Plus, Gully keeps building on it — noticing patterns "
    + "across matches, practices and conversations that would "
    + "otherwise disappear."
  ),
  bullets: [
    "Every future session builds on what Gully already knows",
    "Pattern memory across matches and months",
    "Coaching that gets more personal to your game over time",
  ],
  dismiss_default: "Not right now",
};

export default function ConversationOfferModal({
  sessionId,
  offer,   // { session_number, threshold, cta, dismiss }
  onClose,
  onDismiss,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpgrade = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/web/conversation-offer/checkout",
        { session_id: sessionId, trigger: offer?.trigger || "threshold" });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Couldn't reach Stripe. Try again in a moment.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "checkout_failed";
      setError(
        detail === "not_authenticated"
          ? "Sign in first to continue with Gully Plus."
          : "Couldn't start checkout. Try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    // Fire and forget — the modal closes even if the endpoint fails,
    // and the local dismiss state prevents re-display in this session.
    try {
      await api.post("/web/conversation-offer/dismiss",
        { session_id: sessionId });
    } catch { /* swallow — dismissal is persistent client-side too */ }
    if (onDismiss) onDismiss();
    if (onClose) onClose();
  };

  const isParent = _isParentAudience();
  const copy = isParent ? PARENT_COPY : PLAYER_COPY;
  const sessionCount = offer?.session_number ?? offer?.threshold ?? "";
  const eyebrowLine = copy.eyebrow.replace("{N}", String(sessionCount));

  return (
    <div
      data-testid="conversation-offer-modal"
      data-audience={isParent ? "parent" : "player"}
      role="dialog"
      aria-labelledby="conversation-offer-title"
      className="fixed inset-x-0 bottom-0 z-40 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-2xl">
        <div
          className="text-[11px] uppercase tracking-widest text-emerald-400"
          data-testid="conversation-offer-modal-eyebrow"
        >
          {eyebrowLine}
        </div>
        <div
          id="conversation-offer-title"
          data-testid="conversation-offer-modal-title"
          className="mt-1 text-base font-semibold text-slate-50"
        >
          {copy.title}
        </div>
        {/* Iter XX.205 — Personalised observation callout. Truthful:
            renders ONLY when the backend attached `offer.observation`
            (Gully genuinely noticed something). Falls back silently
            to the generic body when null. */}
        {offer?.observation && (
          <div
            className="mt-3 rounded-xl border border-emerald-900/50 bg-emerald-950/25 px-3 py-2.5"
            data-testid="conversation-offer-modal-observation"
          >
            <div className="text-[10px] uppercase tracking-widest text-emerald-500/90 mb-1">
              What Gully just noticed
            </div>
            <div className="text-[13px] text-emerald-100/95 leading-relaxed">
              {offer.observation}
            </div>
          </div>
        )}
        <div
          className="mt-3 text-[13px] text-slate-400 leading-relaxed"
          data-testid="conversation-offer-modal-body"
        >
          {copy.body}
        </div>
        <ul
          className="mt-3 space-y-1.5 text-[12px] text-slate-400"
          data-testid="conversation-offer-modal-bullets"
        >
          {copy.bullets.map((b) => (
            <li key={b}>· {b}</li>
          ))}
        </ul>
        {error && (
          <div className="mt-3 text-xs text-red-400" data-testid="conversation-offer-modal-error">
            {error}
          </div>
        )}
        <button
          type="button"
          data-testid="conversation-offer-modal-cta"
          onClick={handleUpgrade}
          disabled={loading}
          className="mt-4 w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 transition-colors disabled:opacity-60"
        >
          {loading ? "Opening Stripe…" : (offer?.cta || "Keep Gully learning my cricket")}
        </button>
        <button
          type="button"
          data-testid="conversation-offer-modal-dismiss"
          onClick={handleDismiss}
          className="mt-2 w-full rounded-xl border border-slate-800 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-900 transition-colors"
        >
          {offer?.dismiss || copy.dismiss_default}
        </button>
      </div>
    </div>
  );
}
